import { ProgramDetailLayout } from '@components/buzzkuri/shared/layouts/ProgramDetail'
import { BuzzkuriBreadcrumb } from '@components/buzzkuri/shared/uiParts/breadcrumb/BuzzkuriBreadcrumb'
import {
  requestLink,
  SCROLLING_CONTENTS,
  SCROLLING_CONTENTS_SP,
} from '@constants/buzzkuri/program-detail'
import { ProgramDetailPageRes } from '@type/buzzkuri/program-detail'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ContactUs } from '../Programs/ContactUs'
import { GuestsVoicesSection } from './GuestsVoicesSection'
import { ProgramAim } from './ProgramAim'
import { ProgramDetail } from './ProgramDetail'
import { ProgramExplanation } from './ProgramExplanation'
import { ProgramFeature } from './ProgramFeature'
import { ProgramStepSection } from './ProgramStepSection'
import { ProgramsSlide } from './ProgramsSlide'
import { ProgramDetailsPageSubColumn } from './SubColumn'
import { SummaryProgramSection } from './SummaryProgramSection'
import { CategoryNavList } from '../Programs/CategoryNavList'
import { WindowSizeContext } from '@context/WindowSize/Context'
import { DocumentRequest } from './DocumentRequestBtn'
import { RecommendTrainingProgram } from './SubColumn/RecommendTrainingProgram'
import { BackToTraining } from './BackToTraining'
import { ProgramRequestBtn } from './ProgramRequestBtn'
import { ProductEffect } from './SubColumn/ProductEffect'
import router from 'next/router'
import { cloudfrontPath } from '@utils/image'
import { isValidProps } from '@utils/validate'
import { BREAKPOINTS } from '@constants/common'
import { InitProgramNavList } from '@context/ProgramNavList/InitProgramNavList'
import { TrialSessionLink } from '@components/ui/link/TrialSessionLink'
import { DigestMovie } from './DigestMovie'

export const ProgramDetailsPage = ({
  pageContent,
  business,
}: ProgramDetailPageRes): JSX.Element => {
  const {
    detailProgram,
    recommendPrograms,
    sameObjectivePrograms,
    sameGenrePrograms,
    similarFeaturePrograms,
    totalProgramCount,
    programCategories,
  } = pageContent
  const {
    programBase,
    programExplanation,
    summaryProgram,
    purposes,
    feature,
    steps,
    guestsVoices,
  } = detailProgram

  const { objectives, tags, youtubeId } = programBase
  const { capacity, requiredTime } = programExplanation
  const winSize = useContext(WindowSizeContext)

  const [isBrowser, setIsBrowser] = useState(false)

  useEffect(() => {
    setIsBrowser(true)
  }, [])

  const programId = useMemo(() => {
    if (isBrowser) {
      const paths = router.asPath.split('/')
      const programNumber = paths[paths.length - 1].split('?')[0]
      return Number(programNumber)
    }
    return NaN
  }, [isBrowser])

  InitProgramNavList({ totalProgramCount, programCategories })

  return (
    <ProgramDetailLayout {...{ business }}>
      <>
        <div className="flex flex-col lg:flex-row lg:justify-between items-center lg:items-start lg:mr-[1.6vw] mb-[16vw] lg:mb-0 lg:ml-[5.5vw]">
          <div>
            <div className="mt-[3.2vw] lg:mt-[1.6vw] w-[78.7vw] lg:w-[62.6vw]">
              <BuzzkuriBreadcrumb currentPathName={programBase.title} />
            </div>
            <div className="mt-[4vw] lg:mt-[3.1vw]">
              <ProgramDetail {...detailProgram} />
            </div>
            {isValidProps(purposes) && (
              <div className="mt-[4.5vw] lg:mt-[4vw]">
                <ProgramAim
                  resolveProblems={purposes}
                  iconPath={`${cloudfrontPath}/programs/icons/program-detail/program_aim_check${
                    winSize?.screenTarget === 'sp' ? '_sp' : ''
                  }.svg`}
                />
              </div>
            )}
            {/* ページ内ナビゲーション */}
            {/* <div className="mt-[7.7vw] lg:mt-[5.8vw]">
              <ScrollingTableOfContents {...business} />
            </div> */}
            {isValidProps(youtubeId) && (
              <>
                <div id="digestMovie"></div>
                <div className="mt-[10.7vw] lg:mt-[3.4vw]">
                  <DigestMovie {...{ youtubeId }} />
                </div>
              </>
            )}
            {isValidProps(programExplanation) && (
              <div
                id={SCROLLING_CONTENTS.aboutProgram.targetId}
                className="mt-[8vw] lg:mt-[6.5vw]"
              >
                <ProgramExplanation
                  {...{ ...programExplanation, ...summaryProgram }}
                />
              </div>
            )}
            {isValidProps(feature) &&
              isValidProps(objectives) &&
              isValidProps(tags) && (
                <div
                  id={SCROLLING_CONTENTS.programFeature.targetId}
                  className="mt-[8.8vw] lg:mt-[4.1vw]"
                >
                  <ProgramFeature {...{ feature, objectives, tags }} />
                </div>
              )}
            {isValidProps(steps) && (
              <div
                id={SCROLLING_CONTENTS.programFlow.targetId}
                className="mt-[10.2vw] lg:mt-[6.4vw]"
              >
                <ProgramStepSection {...{ steps }} />
              </div>
            )}
            {isValidProps(guestsVoices) && (
              <div
                id={SCROLLING_CONTENTS.participantVoice.targetId}
                className="mt-[12.5vw] lg:mt-[6.4vw]"
              >
                <GuestsVoicesSection {...{ guestsVoices }} />
              </div>
            )}
            {isValidProps(summaryProgram) &&
              isValidProps(capacity) &&
              isValidProps(requiredTime) && (
                <div
                  id={SCROLLING_CONTENTS.programOverview.targetId}
                  className="mt-[12.5vw] lg:mt-[6.2vw]"
                >
                  <SummaryProgramSection
                    {...{ summaryProgram, capacity, requiredTime }}
                  />
                </div>
              )}
            <div className="mt-[11.5vw] lg:mt-[5.7vw]">
              {business?.eventRequestable ? (
                <div id={SCROLLING_CONTENTS_SP.programRequest.targetId}>
                  <ProgramRequestBtn
                    url={requestLink(business.id, Number(programId))}
                    text="このプログラムをリクエスト"
                  />
                </div>
              ) : (
                <div id={SCROLLING_CONTENTS_SP.contactUs.targetId}>
                  <DocumentRequest />
                </div>
              )}
            </div>
            {winSize?.width <= BREAKPOINTS.laptop && (
              <>
                <div className="mt-[10.5vw]">
                  <ProductEffect />
                </div>
                {/*　PC 開催までの流れ */}
                {!business?.eventRequestable && (
                  <div className="mt-[8vw]">
                    <TrialSessionLink referer="プログラム詳細ページsp" />
                  </div>
                )}
              </>
            )}
            <div
              id={SCROLLING_CONTENTS.relatedProgram.targetId}
              className="mt-[10.7vw] lg:mt-[1.1vw]"
            >
              {winSize?.width > BREAKPOINTS.laptop ? (
                <>
                  {isValidProps(sameObjectivePrograms) && (
                    <ProgramsSlide
                      heading={`「${sameObjectivePrograms.title}」のプログラム(対面/オンライン)`}
                      programThumbnailGroup={sameObjectivePrograms.programs}
                    />
                  )}
                  {isValidProps(sameGenrePrograms) && (
                    <ProgramsSlide
                      heading={`「${sameGenrePrograms.title}」のプログラム(対面/オンライン)`}
                      programThumbnailGroup={sameGenrePrograms.programs}
                    />
                  )}
                  {isValidProps(similarFeaturePrograms) && (
                    <ProgramsSlide
                      heading="よく一緒にチェックされているプログラム(対面/オンライン)"
                      programThumbnailGroup={similarFeaturePrograms}
                    />
                  )}
                </>
              ) : (
                <div className="w-[89.3vw]">
                  {isValidProps(sameObjectivePrograms) && (
                    <RecommendTrainingProgram
                      recommendPrograms={sameObjectivePrograms.programs}
                      heading={`「${sameObjectivePrograms.title}」のプログラム(対面/オンライン)`}
                    />
                  )}
                  {isValidProps(sameGenrePrograms) && (
                    <div className="mt-[16.4vw]">
                      <RecommendTrainingProgram
                        recommendPrograms={sameGenrePrograms.programs}
                        heading={`「${sameGenrePrograms.title}」のプログラム(対面/オンライン)`}
                      />
                    </div>
                  )}
                  {isValidProps(similarFeaturePrograms) && (
                    <div className="mt-[16.4vw]">
                      <RecommendTrainingProgram
                        recommendPrograms={similarFeaturePrograms}
                        heading="よく一緒にチェックされているプログラム(対面/オンライン)"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {winSize?.width > BREAKPOINTS.laptop && (
              <div className="lg:mt-[4.8vw]">
                <BackToTraining />
              </div>
            )}
            <div className="mt-[16.4vw] lg:mt-[7.9vw]">
              <CategoryNavList />
            </div>
          </div>
          {winSize?.width > BREAKPOINTS.laptop && (
            <ProgramDetailsPageSubColumn
              business={business}
              recommendPrograms={recommendPrograms}
              requiredTime={programExplanation.requiredTime}
              venue={summaryProgram.venueDetail}
              programName={detailProgram.programBase.title}
              programId={programId}
            />
          )}
        </div>
        {winSize?.width > BREAKPOINTS.laptop && (
          <div className="lg:mt-[6.3vw] lg:ml-[7.2vw]">
            <ContactUs />
          </div>
        )}
      </>
    </ProgramDetailLayout>
  )
}
